import { RECS_MANAGEMENT_API, IDENTITY_API } from '@/api';
import { createMutations } from '@/utils/vuexHelper';
import RecsBaseStore from '../RecsBaseStore';
import dateStore from '@/utils/dateStore';

const state = {
  screenName: 'transactionsScreen',
  // stands for 180 days in the past
  defaultDateDiff: 180,

  selectedSystemNames: null,
  selectedEntityNames: [],
  selectedTransactionType: null,
  selectedDates: null,
  showCompleted: true,

  transactionTypes: ['external transfer', 'internal transfer', 'retirement'],
  scs: [],
  transactionData: [],
  mappedData: [],

  numRows: 0,
  numRecs: 0,

  transactionTableData: [],
  transactionConfig: {
    name: 'recTransactions',
    columns: [
      {
        prop: 'certificate.serialNumberBase', label: 'Serial# Base', sortable: true, filterable: true,
      },
      {
        prop: 'systemName', label: 'System Name', sortable: true, filterable: true,
      },
      {
        prop: 'serialNumberStart', label: 'Serial # Start', sortable: true, filterable: true,
      },
      {
        prop: 'serialNumberEnd', label: 'Serial # End', sortable: true, filterable: true,
      },
      {
        prop: 'fromAccount', label: 'From Acct', sortable: true, filterable: true,
      },
      {
        prop: 'fromOrganization', label: 'Source Org', sortable: true, filterable: true,
      },
      {
        prop: 'toAccount', label: 'To Acct', sortable: true, filterable: true,
      },
      {
        prop: 'toOrganization', label: 'Destination Org', sortable: true, filterable: true,
      },
      {
        prop: 'certificate.generator.mretsId', label: 'GU ID', sortable: true, filterable: true,
      },
      {
        prop: 'certificate.generator.stateProvince', label: 'Generator Location', sortable: true, filterable: true,
      },
      {
        prop: 'certificate.generator.name', label: 'Generator', sortable: true, filterable: true,
      },
      {
        prop: 'certificate.vintageDate', label: 'Vintage Date', sortable: true, filterable: true,
      },
      {
        prop: 'certificate.fuelType.name', label: 'Fuel Type', sortable: true, filterable: true,
      },
      {
        prop: 'transactionType', label: 'Transaction Type', sortable: true, filterable: true, //  groupIndex: 0,
      },
      {
        prop: 'startedAt', label: 'Date Started', sortable: true, filterable: true,
      },
      {
        prop: 'endedAt', label: 'Date Completed', sortable: true, filterable: true,
      },
      {
        prop: 'status', label: 'Status', sortable: true, filterable: true,
      },
      {
        prop: 'quantity', label: 'Qty', sortable: true, filterable: true,
      },
      {
        prop: 'retirementKind', label: 'Retirement Kind', sortable: true, filterable: true,
      },
      {
        prop: 'exportOrganization.name', label: 'Export Org', sortable: true, filterable: true,
      },
      {
        prop: 'hourlyClaim', label: 'Hourly Claim', sortable: true, filterable: true,
      },
      {
        prop: 'compliancePeriod', label: 'Compliance Period', sortable: true, filterable: true, visible: false,
      },
      {
        prop: 'retirementType', label: 'Retirement Type', sortable: true, filterable: true, visible: false,
      },
      {
        prop: 'retiredTo', label: 'Retired To', sortable: true, filterable: true, visible: false,
      },
      {
        prop: 'retiredQuarter', label: 'Retired Quarter', sortable: true, filterable: true, visible: false,
      },
      {
        prop: 'retirementReason', label: 'Retirement Reason', sortable: true, filterable: true, visible: false,
      },
      {
        prop: 'exportSystem', label: 'Export System', sortable: true, filterable: true, visible: false,
      },
      {
        prop: 'isMultiAccountDestination', label: 'Multi Account Destination', sortable: true, filterable: true, visible: false,
      },
      {
        prop: 'user.name', label: 'User', sortable: true, filterable: true, visible: false,
      },
      {
        prop: 'endedByUser.name', label: 'Ended By User', sortable: true, filterable: true, visible: false,
      },
      {
        prop: 'retirementOption', label: 'Retirement Option', sortable: true, filterable: true, visible: false,
      },
      {
        prop: 'notes', label: 'Notes', sortable: true, filterable: true, visible: false,
      },
    ],
    options: {
      columnConfig: true,
      filterHeader: true,
      groupPanel: true,
      exportExcel: true,
    },
  },
  ...RecsBaseStore.state,
};

const _getList = (options, key) => options.map((opt) => ({ value: opt[key], label: opt[key] }));

const getters = {
  scList: (state) => _getList(state.scs, 'name'),
  currentSearchParams(state, getters) {
    
    const { dateRangeDiff, endDate } = getters.currentDateRangeSearchParams;
    const searchParams = {
      systemName: state.selectedSystemNames,
      entityNames: state.selectedEntityNames,
      transactionType: state.selectedTransactionType,
      dateRangeDiff,
      endDate,
    }
    return searchParams;
  },
  ...RecsBaseStore.getters,
};

const actions = {
  async initialize({ commit, dispatch }) {
    commit('resetTable');
    await dispatch('fetchScs');
    await dispatch('initSearchParams');
  },
  resetTable({ commit }) {
    commit('resetTable');
  },
  async loadTableData({ dispatch, commit }) {
    commit('resetTable');
    await dispatch('loadTransactionData');
  },
  async fetchScs({ commit }) {
    try {
      const { data: { entities } } = await IDENTITY_API.get('entities');
      commit('setScs', entities.filter((x) => x.type === 'COMPANY'));
    } catch (error) {
      this.$notify('Failed to fetch SCs', 'error');
      console.error(error);
    }
  },
  async loadTransactionData({ state, commit, dispatch }) {
    const tz = dateStore.getTimeZone();
    const momentStartDate = dateStore.toDateFromLocal(state.selectedDates[0], tz);
    const momentEndDate = dateStore.toDateFromLocal(state.selectedDates[1], tz);
    await Promise.all(state.selectedEntityNames.map(async (entityName) => {
      const params = {
        systemName: state.selectedSystemNames,
        entityName,
        transactionType: state.selectedTransactionType,
        startDate: momentStartDate.toISOString(),
        endDate: momentEndDate.add(1, 'days').toISOString(),
      };
      await RECS_MANAGEMENT_API.get('/recs-management/transaction/transactionDetails', { params }).then(({ data }) => {
        commit('addTransactionDetailData', data);
      }).catch((err) => {
        this.$notify('Failed to Load Transaction Details', 'error');
      });
    }));
    this.$notify(`${state.transactionData.length} Transaction Details Loaded`, 'info');

    await dispatch('mapTransactionData');

    if (state.showCompleted) {
      await dispatch('showCompletedTransactions');
    } else {
      await dispatch('showAllTransactions');
    }
  },
  mapTransactionData({ state }) {
    const combinedData = state.transactionData.reduce((acc, item) => {
      const { ...detail } = item;
      const { transaction } = detail;
      delete detail.transaction;
      const existingItem = acc.find((x) => x.transaction.id === transaction.id);
      if (existingItem) {
        existingItem.details.push(detail);
      } else {
        acc.push({ transaction, details: [detail] });
      }
      return acc;
    }, []);

    state.mappedData = combinedData.reduce((acc, item) => {
      const { transaction, details } = item;
      // flattens transactionData into a list of transactions and their details to match table config
      const transactionRow = details.map((detail) => ({ ...transaction, ...detail }));
      const editedRow = transactionRow.map((row) => {
        // can alter row data here such as converting to date only format
        row.startedAt = row.startedAt ? row.startedAt.split('T')[0] : null;
        row.endedAt = row.startedAt ? row.endedAt?.split('T')[0] : null;
        return row;
      });
      acc.push(...editedRow);
      return acc;
    }, []);
  },
  async showCompletedTransactions({ state, commit }) {
    const completedTransactions = state.mappedData.filter((x) => x.status === 'complete');
    await commit('setTransactionTableData', completedTransactions);
  },
  async showAllTransactions({ state, dispatch, commit }) {
    await commit('setTransactionTableData', state.mappedData);
  },
  async getSavedSearchParams({ state, dispatch }) {
    const params = await dispatch('getRecsSearchParams', state.screenName);
    return params;
  },
  setDefaultSearchParams({ commit, state, getters }) {
    const defaultDateRange = getters.defaultDateRange;
    const params = {
      startDate: defaultDateRange[0],
      endDate: defaultDateRange[1],
    }
    commit('setSearchParams', params);
  },
  async initSearchParams({ commit, state, dispatch, getters }) {
    const savedParams = await dispatch('getSavedSearchParams');
    if(savedParams) {      
      let endDate = savedParams.endDate ?? getters.defaultEndDate;
      endDate = dateStore.toDateFromLocal(endDate);
      const dateDiff = savedParams.dateRangeDiff ?? state.defaultDateDiff;
      const startDate = endDate.clone().subtract(dateDiff, 'days');

      const params = {
        systemName: savedParams.systemName,
        entityNames: savedParams.entityNames,
        transactionType: savedParams.transactionType,
        endDate,
        startDate,
      };

      commit('setSearchParams', params);
    } else {
      dispatch('setDefaultSearchParams');
    }
  },
  async saveSearchParams({ state, dispatch, getters }) {
    const searchParams = getters.currentSearchParams;
    const savePayload =  { 
      resource: state.screenName, 
      payload: searchParams 
    };
    await dispatch('saveRecsSearchParams', savePayload);
    return searchParams;
  },
  ...RecsBaseStore.actions,
};

const mutations = {
  resetTable(state) {
    state.transactionTableData = [];
    state.transactionData = [];
    state.numRows = 0;
    state.numRecs = 0;
  },
  setSelectedSystems(state, value) {
    state.selectedSystemNames = value;
  },
  setSelectedEntities(state, value) {
    state.selectedEntityNames = value;
  },
  setTransactionTableData(state, transactionData) {
    state.transactionTableData = transactionData;

    state.numRows = state.transactionTableData.length;
    state.numRecs = state.transactionTableData.reduce((acc, item) => acc + item.quantity, 0);
  },
  setCurrentRows(state, value) {
    state.numRows = value.length;
    state.numRecs = value.reduce((acc, item) => acc + item.quantity, 0);
  },
  setSelectedDates(state, value) {
    state.selectedDates = value;
  },
  addTransactionDetailData(state, transactionDetails) {
    state.transactionData = state.transactionData.concat(transactionDetails);
  },
  setSearchParams(state, value) {
    state.selectedSystemNames = value.systemName;
    state.selectedEntityNames = value.entityNames;
    state.selectedTransactionType = value.transactionType;
    state.selectedDates = [value.startDate, value.endDate];
  },
  setShowCompleted(state, value) {
    state.showCompleted = value;
  },
  ...createMutations(
    'scs',
    'selectedTransactionType',
  ),
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};